"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, X } from "lucide-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { cn } from "@/lib/utils";

dayjs.extend(customParseFormat);

/** Parse a free-text date string using common formats. Returns a valid dayjs or null. */
function parseInputDate(raw: string): dayjs.Dayjs | null {
  const formats = [
    "DD/MM/YYYY",
    "D/M/YYYY",
    "D/M/YY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "D MMM YYYY",
    "D MMMM YYYY",
  ];
  for (const fmt of formats) {
    const parsed = dayjs(raw, fmt, true);
    if (parsed.isValid()) return parsed;
  }
  // No loose fallback — partial/ambiguous strings like "09/04/" must not produce a date
  return null;
}

/**
 * Apply a DD/MM/YYYY mask to raw keyboard input with segment clamping.
 *
 * Clamping rules (mirrors MUI DatePicker behaviour):
 *  - Day  : 1–31. If the first digit > 3 → auto-prepend 0 (e.g. "4" → "04").
 *            If both digits are entered and value > 31 → keep only the units
 *            digit and prepend 0 (e.g. "39" → "09", "99" → "09").
 *  - Month: 1–12. If the first digit > 1 → auto-prepend 0 (e.g. "5" → "05").
 *            If both digits are entered and value > 12 → keep only the units
 *            digit and prepend 0 (e.g. "19" → "09").
 *  - Year : no clamping, limit to 4 digits.
 *
 * Handles backspace at slash boundaries so deleting a slash doesn't re-insert it.
 */
function formatDateMask(raw: string, prev: string): string {
  const isDeleting = prev.length > raw.length;

  // Strip non-digits; we work purely on the digit stream
  let digits = raw.replace(/\D/g, "");

  // --- Day segment (digits[0..1]) ---
  if (digits.length >= 1) {
    const d0 = parseInt(digits[0], 10);
    if (d0 > 3) {
      // First digit impossible as tens → auto-prefix 0 and shift
      digits = "0" + digits;
    }
  }
  if (digits.length >= 2) {
    const dayVal = parseInt(digits.slice(0, 2), 10);
    if (dayVal > 31) {
      // Keep only units digit, prefix 0
      digits = "0" + digits[1] + digits.slice(2);
    } else if (dayVal === 0) {
      // 00 is not a valid day → clamp to 01
      digits = "01" + digits.slice(2);
    }
  }

  // --- Month segment (digits[2..3]) ---
  if (digits.length >= 3) {
    const m0 = parseInt(digits[2], 10);
    if (m0 > 1) {
      // First month digit > 1 is impossible as tens → auto-prefix 0
      digits = digits.slice(0, 2) + "0" + digits.slice(2);
    }
  }
  if (digits.length >= 4) {
    const monthVal = parseInt(digits.slice(2, 4), 10);
    if (monthVal > 12) {
      digits = digits.slice(0, 2) + "0" + digits[3] + digits.slice(4);
    } else if (monthVal === 0) {
      digits = digits.slice(0, 2) + "01" + digits.slice(4);
    }
  }

  // --- Year segment (digits[4..7]) — reset to current year if outside 1900–2100 ---
  if (digits.length >= 8) {
    const yearVal = parseInt(digits.slice(4, 8), 10);
    const currentYear = dayjs().year();
    const resolvedYear = yearVal < 1900 || yearVal > 2100 ? currentYear : yearVal;
    digits = digits.slice(0, 4) + String(resolvedYear).padStart(4, "0") + digits.slice(8);
  }

  // Limit to 8 digits (DD MM YYYY)
  digits = digits.slice(0, 8);

  // Build the masked string with slashes
  let result = "";
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += "/";
    result += digits[i];
  }

  // Backspace at slash boundary: if user deleted a trailing slash, don't re-add it
  const deletedTrailingSlash =
    isDeleting && !raw.endsWith("/") && (prev === result + "/" || prev.startsWith(result + "/"));
  if (deletedTrailingSlash && result.endsWith("/")) {
    result = result.slice(0, -1);
  }

  return result;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface DatePickerV2Props {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Extra classes merged onto the trigger wrapper div (overrides defaults like h-9, border-input, rounded-md). */
  triggerClassName?: string;
  showTime?: boolean;
  range?: boolean;
  rangeValue?: DateRange | null;
  onRangeChange?: (range: DateRange | null) => void;
  monthOnly?: boolean;
  dateFormat?: string;
  hasError?: boolean;
  onBlur?: () => void;
}

type ViewMode = "day" | "month" | "year";

export function DatePickerV2({
  value = null,
  onChange,
  disabled = false,
  placeholder = "Select date and time",
  className,
  triggerClassName,
  showTime = true,
  range = false,
  rangeValue = null,
  onRangeChange,
  monthOnly = false,
  dateFormat = "DD/MM/YYYY",
  hasError = false,
  onBlur,
}: DatePickerV2Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(monthOnly ? "month" : "day");
  const [currentDate, setCurrentDate] = useState<Date>(value || rangeValue?.from || new Date());
  const [tempHours, setTempHours] = useState(value?.getHours() || 0);
  const [tempMinutes, setTempMinutes] = useState(value?.getMinutes() || 0);
  const [rangeStartDate, setRangeStartDate] = useState<Date | null>(rangeValue?.from || null);
  const [rangeEndDate, setRangeEndDate] = useState<Date | null>(rangeValue?.to || null);
  const [selectingRangeEnd, setSelectingRangeEnd] = useState(false);
  // Keyboard input state: null = show formatted value
  const [inputText, setInputText] = useState<string | null>(null);
  const [rangeFromInputText, setRangeFromInputText] = useState<string | null>(null);
  const [rangeToInputText, setRangeToInputText] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerContainerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
    width: number;
    placement: "top" | "bottom";
  } | null>(null);

  const resolvePortalContainer = () => {
    if (typeof document === "undefined") {
      return null;
    }

    const sheetContent = triggerContainerRef.current?.closest(
      '[data-slot="sheet-content"]'
    ) as HTMLElement | null;

    return sheetContent ?? document.body;
  };

  const valueTimestamp = value ? value.getTime() : null;
  const rangeFromTimestamp = rangeValue?.from ? rangeValue.from.getTime() : null;
  const rangeToTimestamp = rangeValue?.to ? rangeValue.to.getTime() : null;

  // Calculate popover position with auto-placement
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && triggerContainerRef.current) {
        const triggerRect = triggerContainerRef.current.getBoundingClientRect();
        const popoverHeight = popoverRef.current?.offsetHeight || 400; // estimated height
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const gap = 8;

        // Determine placement: prefer bottom, but use top if not enough space
        const placement =
          spaceBelow >= popoverHeight + gap
            ? "bottom"
            : spaceAbove >= popoverHeight + gap
              ? "top"
              : spaceBelow >= spaceAbove
                ? "bottom"
                : "top";

        const popoverWidth = popoverRef.current?.offsetWidth || 288; // w-72 = 288px
        const viewportWidth = window.innerWidth;

        const top =
          placement === "bottom" ? triggerRect.bottom + gap : triggerRect.top - popoverHeight - gap;

        // Clamp left so the popover never bleeds off the right edge
        const rawLeft = triggerRect.left;
        const left = Math.min(rawLeft, viewportWidth - popoverWidth - 8);

        setPopoverPosition({
          top,
          left,
          width: triggerRect.width,
          placement,
        });
      }
    };

    // Initial position calculation
    if (isOpen) {
      // Calculate immediately for initial render
      updatePosition();
      // Recalculate after a brief delay to adjust for actual popover height
      setTimeout(updatePosition, 10);
    }

    if (isOpen) {
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Sync single-date state when controlled value changes (e.g., form reset).
  useEffect(() => {
    if (range) {
      return;
    }

    setInputText(null);

    if (value) {
      setTempHours(value.getHours());
      setTempMinutes(value.getMinutes());
      setCurrentDate(value);
      return;
    }

    const now = new Date();
    setCurrentDate(now);
    setTempHours(0);
    setTempMinutes(0);
  }, [range, valueTimestamp]);

  // Sync range state when controlled range value changes (e.g., form reset).
  useEffect(() => {
    if (!range) {
      return;
    }

    setRangeFromInputText(null);
    setRangeToInputText(null);
    setSelectingRangeEnd(false);

    if (rangeValue?.from) {
      setCurrentDate(rangeValue.from);
    }

    setRangeStartDate(rangeValue?.from || null);
    setRangeEndDate(rangeValue?.to || null);
  }, [range, rangeFromTimestamp, rangeToTimestamp]);

  // Handle outside clicks
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // Check if click is outside both container and popover
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);

      if (isOutsideContainer && isOutsidePopover) {
        setIsOpen(false);
      }
    };

    // Use timeout to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectMonth = (month: number) => {
    const newDate = new Date(currentDate.getFullYear(), month, 1);
    newDate.setHours(0, 0, 0, 0);

    if (monthOnly) {
      onChange?.(newDate);
      setIsOpen(false);
    } else {
      setCurrentDate(newDate);
      setViewMode("day");
    }
  };

  const handleSelectDay = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    newDate.setHours(showTime ? tempHours : 0, showTime ? tempMinutes : 0, 0, 0);

    if (range) {
      if (!selectingRangeEnd) {
        setRangeStartDate(newDate);
        setSelectingRangeEnd(true);
      } else {
        const from = newDate < rangeStartDate! ? newDate : rangeStartDate!;
        const to = newDate < rangeStartDate! ? rangeStartDate! : newDate;
        setRangeStartDate(from);
        setRangeEndDate(to);
        setSelectingRangeEnd(false);
        onRangeChange?.({ from, to });
        setIsOpen(false);
      }
    } else {
      // Update current date for display
      setCurrentDate(newDate);

      // If showTime is enabled, don't close - let user adjust time and click Confirm
      if (showTime) {
        // Just update the selected date, don't close or trigger onChange yet
        return;
      }

      // If no time selection needed, trigger onChange and close immediately
      onChange?.(newDate);
      setIsOpen(false);
    }
  };

  const handleSelectYear = (year: number) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setViewMode("month");
  };

  const MIN_YEAR = 1900;
  const MAX_YEAR = 2100;

  const handlePrevious = () => {
    if (viewMode === "day") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1));
    } else if (viewMode === "year") {
      const startYear = Math.floor(currentDate.getFullYear() / 12) * 12;
      if (startYear - 12 + 11 < MIN_YEAR) return; // already at first page
      setCurrentDate(new Date(startYear - 12, 0, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === "day") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
    } else if (viewMode === "year") {
      const startYear = Math.floor(currentDate.getFullYear() / 12) * 12;
      if (startYear + 12 > MAX_YEAR) return; // already at last page
      setCurrentDate(new Date(startYear + 12, 0, 1));
    }
  };

  const handleTimeChange = (type: "hours" | "minutes", val: number) => {
    if (type === "hours") {
      const newHours = Math.max(0, Math.min(23, val));
      setTempHours(newHours);
      // Update currentDate with new hours
      const newDate = new Date(currentDate);
      newDate.setHours(newHours, tempMinutes, 0, 0);
      setCurrentDate(newDate);
    } else {
      const newMinutes = Math.max(0, Math.min(59, val));
      setTempMinutes(newMinutes);
      // Update currentDate with new minutes
      const newDate = new Date(currentDate);
      newDate.setHours(tempHours, newMinutes, 0, 0);
      setCurrentDate(newDate);
    }
  };

  const handleConfirmDateTime = () => {
    if (range) {
      if (rangeStartDate && rangeEndDate) {
        onRangeChange?.({ from: rangeStartDate, to: rangeEndDate });
      }
    } else {
      const newDate = new Date(currentDate);
      newDate.setHours(showTime ? tempHours : 0, showTime ? tempMinutes : 0, 0, 0);
      onChange?.(newDate);
    }
    setIsOpen(false);
  };

  // Render day view
  const renderDayView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="aero:space-y-3">
        {/* Header */}
        <div className="aero:flex aero:items-center aero:justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors"
          >
            <ChevronLeft className="aero:w-5 aero:h-5" />
          </button>
          <div className="aero:flex aero:gap-2">
            <button
              type="button"
              onClick={() => setViewMode("month")}
              className="aero:px-3 aero:py-1 aero:text-sm aero:font-medium aero:hover:bg-muted aero:rounded aero:transition-colors"
            >
              {dayjs(currentDate).format("MMMM")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("year")}
              className="aero:px-3 aero:py-1 aero:text-sm aero:font-medium aero:hover:bg-muted aero:rounded aero:transition-colors"
            >
              {dayjs(currentDate).format("YYYY")}
            </button>
          </div>
          <button
            type="button"
            onClick={handleNext}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors"
          >
            <ChevronRight className="aero:w-5 aero:h-5" />
          </button>
        </div>

        {/* Day labels */}
        <div className="aero:grid aero:grid-cols-7 aero:gap-0.5 aero:text-center aero:text-[0.7rem] aero:font-medium aero:text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day}>{day}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="aero:grid aero:grid-cols-7 aero:gap-0.5">
          {weeks.map((week, weekIndex) =>
            week.map((day, dayIndex) => (
              <button
                type="button"
                key={`${weekIndex}-${dayIndex}`}
                onClick={() => day && handleSelectDay(day)}
                disabled={!day || disabled}
                className={cn(
                  "aero:w-8 aero:h-8 aero:text-xs aero:rounded aero:transition-colors aero:flex aero:items-center aero:justify-center",
                  day
                    ? "aero:hover:bg-accent aero:hover:text-accent-foreground aero:cursor-pointer"
                    : "aero:text-muted-foreground",
                  // Highlight selected day - check currentDate for showTime mode, value for normal mode
                  day &&
                    !range &&
                    (showTime
                      ? dayjs(
                          new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                        ).isSame(currentDate, "day")
                      : value &&
                        dayjs(
                          new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                        ).isSame(value, "day"))
                    ? "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-bold"
                    : day &&
                        range &&
                        rangeStartDate &&
                        dayjs(
                          new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                        ).isSame(rangeStartDate, "day")
                      ? "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-bold"
                      : day &&
                          range &&
                          rangeEndDate &&
                          dayjs(
                            new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                          ).isSame(rangeEndDate, "day")
                        ? "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-bold"
                        : day &&
                            range &&
                            rangeStartDate &&
                            rangeEndDate &&
                            new Date(currentDate.getFullYear(), currentDate.getMonth(), day) >
                              rangeStartDate &&
                            new Date(currentDate.getFullYear(), currentDate.getMonth(), day) <
                              rangeEndDate
                          ? "aero:bg-brand-primary/20 aero:text-brand-primary"
                          : day &&
                              dayjs(
                                new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                              ).isSame(dayjs(), "day")
                            ? "aero:border aero:border-brand-primary"
                            : ""
                )}
              >
                {day}
              </button>
            ))
          )}
        </div>
      </div>
    );
  };

  // Render month view
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className="aero:space-y-4">
        <div className="aero:flex aero:items-center aero:justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors"
          >
            <ChevronLeft className="aero:w-5 aero:h-5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode("year")}
            className="aero:px-3 aero:py-1 aero:text-sm aero:font-medium aero:hover:bg-muted aero:rounded aero:transition-colors"
          >
            {year}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors"
          >
            <ChevronRight className="aero:w-5 aero:h-5" />
          </button>
        </div>

        <div className="aero:grid aero:grid-cols-3 aero:gap-2 aero:min-h-[13rem]">
          {months.map((month, index) => {
            const isSelectedMonth =
              monthOnly &&
              value &&
              value.getMonth() === index &&
              value.getFullYear() === currentDate.getFullYear();
            const isCurrentViewMonth = !monthOnly && currentDate.getMonth() === index;

            return (
              <button
                type="button"
                key={month}
                onClick={() => handleSelectMonth(index)}
                className={cn(
                  "aero:h-10 aero:text-xs aero:rounded aero:transition-colors",
                  isSelectedMonth || isCurrentViewMonth
                    ? "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-bold"
                    : "aero:hover:bg-accent aero:hover:text-accent-foreground"
                )}
              >
                {month.slice(0, 3)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render year view
  const renderYearView = () => {
    const year = currentDate.getFullYear();
    const startYear = Math.floor(year / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);
    const canGoPrev = startYear - 1 >= MIN_YEAR;
    const canGoNext = startYear + 12 <= MAX_YEAR;

    return (
      <div className="aero:space-y-3">
        <div className="aero:flex aero:items-center aero:justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={!canGoPrev}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors aero:disabled:opacity-30 aero:disabled:pointer-events-none"
          >
            <ChevronLeft className="aero:w-5 aero:h-5" />
          </button>
          <span className="aero:text-sm aero:font-medium">
            {startYear} - {startYear + 11}
          </span>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext}
            className="aero:p-1 aero:hover:bg-muted aero:rounded aero:transition-colors aero:disabled:opacity-30 aero:disabled:pointer-events-none"
          >
            <ChevronRight className="aero:w-5 aero:h-5" />
          </button>
        </div>

        <div className="aero:grid aero:grid-cols-3 aero:gap-2 aero:min-h-[13rem]">
          {years.map((y) => {
            const outOfRange = y < MIN_YEAR || y > MAX_YEAR;
            return (
              <button
                key={y}
                type="button"
                disabled={outOfRange}
                onClick={() => !outOfRange && handleSelectYear(y)}
                className={cn(
                  "aero:h-10 aero:text-xs aero:rounded aero:transition-colors",
                  outOfRange
                    ? "aero:opacity-30 aero:cursor-not-allowed"
                    : currentDate.getFullYear() === y
                      ? "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-bold"
                      : "aero:hover:bg-accent aero:hover:text-accent-foreground"
                )}
              >
                {y}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const portalContainer = resolvePortalContainer();

  return (
    <div ref={containerRef} className={cn("aero:relative aero:w-full", className)}>
      {/* Input trigger */}
      {range ? (
        /* Range mode: two side-by-side text inputs */
        <div
          ref={triggerContainerRef}
          className={cn(
            "aero:flex aero:items-center aero:w-full aero:h-9 aero:border aero:border-input aero:rounded-md aero:bg-background aero:shadow-xs aero:transition-[color,box-shadow] aero:focus-within:border-ring aero:focus-within:ring-ring/50 aero:focus-within:ring-[0.5]",
            hasError &&
              "aero:border-destructive aero:ring-destructive/20 aero:ring-[0.5] aero:focus-within:border-destructive aero:focus-within:ring-destructive/20",
            disabled && "aero:opacity-50 aero:pointer-events-none",
            triggerClassName,
            className
          )}
        >
          <input
            type="text"
            disabled={disabled}
            placeholder="From (DD/MM/YYYY)"
            value={
              rangeFromInputText !== null
                ? rangeFromInputText
                : rangeStartDate
                  ? dayjs(rangeStartDate).format(dateFormat)
                  : ""
            }
            onChange={(e) => {
              const masked = formatDateMask(e.target.value, rangeFromInputText ?? "");
              setRangeFromInputText(masked);
              if (masked === "") {
                setRangeStartDate(null);
                onRangeChange?.({ from: null, to: rangeEndDate });
              } else {
                const parsed = parseInputDate(masked);
                if (parsed) {
                  const newFrom = parsed.toDate();
                  setRangeStartDate(newFrom);
                  onRangeChange?.({ from: newFrom, to: rangeEndDate });
                }
              }
            }}
            onBlur={() => {
              if (
                rangeFromInputText !== null &&
                rangeFromInputText !== "" &&
                !parseInputDate(rangeFromInputText)
              ) {
                setRangeStartDate(null);
                onRangeChange?.({ from: null, to: rangeEndDate });
              }
              setRangeFromInputText(null);
              onBlur?.();
            }}
            className="aero:flex-1 aero:min-w-0 aero:h-full aero:bg-transparent aero:pl-3 aero:pr-1 aero:text-sm aero:text-neutral-900 aero:placeholder:text-muted-foreground aero:outline-none"
          />
          <span className="aero:text-muted-foreground aero:text-sm aero:px-1">–</span>
          <input
            type="text"
            disabled={disabled}
            placeholder="To (DD/MM/YYYY)"
            value={
              rangeToInputText !== null
                ? rangeToInputText
                : rangeEndDate
                  ? dayjs(rangeEndDate).format(dateFormat)
                  : ""
            }
            onChange={(e) => {
              const masked = formatDateMask(e.target.value, rangeToInputText ?? "");
              setRangeToInputText(masked);
              if (masked === "") {
                setRangeEndDate(null);
                onRangeChange?.({ from: rangeStartDate, to: null });
              } else {
                const parsed = parseInputDate(masked);
                if (parsed) {
                  const newTo = parsed.toDate();
                  setRangeEndDate(newTo);
                  onRangeChange?.({ from: rangeStartDate, to: newTo });
                }
              }
            }}
            onBlur={() => {
              if (
                rangeToInputText !== null &&
                rangeToInputText !== "" &&
                !parseInputDate(rangeToInputText)
              ) {
                setRangeEndDate(null);
                onRangeChange?.({ from: rangeStartDate, to: null });
              }
              setRangeToInputText(null);
              onBlur?.();
            }}
            className="aero:flex-1 aero:min-w-0 aero:h-full aero:bg-transparent aero:pl-1 aero:pr-2 aero:text-sm aero:text-neutral-900 aero:placeholder:text-muted-foreground aero:outline-none"
          />
          <div className="aero:flex aero:items-center aero:gap-1 aero:pr-2 aero:flex-shrink-0">
            {(rangeStartDate || rangeEndDate) && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRangeChange?.(null);
                  setRangeStartDate(null);
                  setRangeEndDate(null);
                  setSelectingRangeEnd(false);
                  setRangeFromInputText(null);
                  setRangeToInputText(null);
                }}
                className="aero:rounded-sm aero:opacity-70 hover:aero:opacity-100 aero:transition-opacity"
                aria-label="Clear date range"
              >
                <X className="aero:h-3 aero:w-3" />
              </button>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => setIsOpen(!isOpen)}
              className="aero:text-muted-foreground aero:hover:text-foreground aero:transition-colors"
              aria-label="Open date picker"
            >
              <CalendarIcon className="aero:h-4 aero:w-4" />
            </button>
          </div>
        </div>
      ) : (
        /* Single / month-only mode: text input + icon */
        <div
          ref={triggerContainerRef}
          className={cn(
            "aero:flex aero:items-center aero:w-full aero:h-9 aero:border aero:border-input aero:rounded-md aero:bg-background aero:shadow-xs aero:transition-[color,box-shadow] aero:focus-within:border-ring aero:focus-within:ring-ring/50 aero:focus-within:ring-[0.5]",
            hasError &&
              "aero:border-destructive aero:ring-destructive/20 aero:ring-[0.5] aero:focus-within:border-destructive aero:focus-within:ring-destructive/20",
            disabled && "aero:opacity-50 aero:pointer-events-none",
            triggerClassName,
            className
          )}
        >
          {monthOnly ? (
            /* Month-only: not free-text, just show value and open picker on click */
            <span
              className={cn(
                "aero:flex-1 aero:pl-3 aero:text-sm aero:leading-5",
                value ? "aero:text-neutral-900" : "aero:text-muted-foreground"
              )}
            >
              {value ? dayjs(value).format("MMMM YYYY") : placeholder}
            </span>
          ) : (
            <input
              type="text"
              disabled={disabled}
              placeholder={placeholder}
              value={
                inputText !== null
                  ? inputText
                  : value
                    ? showTime
                      ? dayjs(value).format("DD/MM/YYYY HH:mm")
                      : dayjs(value).format(dateFormat)
                    : ""
              }
              onChange={(e) => {
                const masked = formatDateMask(e.target.value, inputText ?? "");
                setInputText(masked);
                if (masked === "") {
                  onChange?.(null);
                } else {
                  const parsed = parseInputDate(masked);
                  if (parsed) {
                    const newDate = parsed.toDate();
                    if (showTime && value) {
                      // preserve existing time
                      newDate.setHours(value.getHours(), value.getMinutes(), 0, 0);
                    }
                    onChange?.(newDate);
                  }
                }
              }}
              onBlur={() => {
                // If user left a non-empty string that didn't resolve to a date,
                // clear the value so Zod validation can catch the missing date.
                if (inputText !== null && inputText !== "" && !parseInputDate(inputText)) {
                  onChange?.(null);
                }
                setInputText(null);
                onBlur?.();
              }}
              className="aero:flex-1 aero:min-w-0 aero:h-full aero:bg-transparent aero:pl-3 aero:pr-2 aero:text-sm aero:text-neutral-900 aero:placeholder:text-muted-foreground aero:outline-none"
            />
          )}
          <div className="aero:flex aero:items-center aero:gap-1 aero:pr-2 aero:flex-shrink-0">
            {value && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(null);
                  setInputText(null);
                  setIsOpen(false);
                }}
                className="aero:rounded-sm aero:opacity-70 hover:aero:opacity-100 aero:transition-opacity"
                aria-label="Clear date"
              >
                <X className="aero:h-3 aero:w-3" />
              </button>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => setIsOpen(!isOpen)}
              className="aero:text-muted-foreground aero:hover:text-foreground aero:transition-colors"
              aria-label="Open date picker"
            >
              <CalendarIcon className="aero:h-4 aero:w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Popover - render via portal */}
      {isOpen &&
        popoverPosition &&
        portalContainer &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              zIndex: 9999,
              pointerEvents: "auto",
            }}
            className="aero:bg-background aero:border aero:border-border aero:rounded-lg aero:shadow-lg aero:w-72 aero:max-h-[calc(100vh-100px)] aero:overflow-y-auto aero:p-3"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Day View */}
            {viewMode === "day" && !monthOnly && renderDayView()}
            {/* Month View */}
            {viewMode === "month" && renderMonthView()}
            {/* Year View */}
            {viewMode === "year" && renderYearView()}

            {/* Time picker */}
            {showTime && !range && !monthOnly && (
              <div className="aero:border-t aero:border-border aero:pt-3">
                <div className="aero:flex aero:items-center aero:gap-2 aero:mb-2">
                  <Clock className="aero:w-4 aero:h-4 aero:text-muted-foreground" />
                  <span className="aero:text-xs aero:font-medium">Time</span>
                </div>
                <div className="aero:flex aero:items-center aero:gap-3">
                  <div className="aero:flex aero:flex-col aero:items-center aero:gap-2">
                    <label className="aero:text-xs aero:text-muted-foreground">Hours</label>
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <button
                        type="button"
                        onClick={() => handleTimeChange("hours", tempHours - 1)}
                        className="aero:p-1 aero:hover:bg-muted aero:rounded"
                      >
                        <ChevronLeft className="aero:w-4 aero:h-4" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={String(tempHours).padStart(2, "0")}
                        onChange={(e) => handleTimeChange("hours", parseInt(e.target.value) || 0)}
                        className="aero:w-12 aero:text-center aero:px-2 aero:py-1 aero:border aero:border-input aero:rounded aero:bg-background aero:text-sm aero:[appearance:textfield] aero:[-moz-appearance:textfield] aero:[&::-webkit-inner-spin-button]:appearance-none aero:[&::-webkit-inner-spin-button]:m-0 aero:[&::-webkit-outer-spin-button]:appearance-none aero:[&::-webkit-outer-spin-button]:m-0"
                      />
                      <button
                        type="button"
                        onClick={() => handleTimeChange("hours", tempHours + 1)}
                        className="aero:p-1 aero:hover:bg-muted aero:rounded"
                      >
                        <ChevronRight className="aero:w-4 aero:h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="aero:text-2xl aero:font-bold aero:text-muted-foreground">:</div>

                  <div className="aero:flex aero:flex-col aero:items-center aero:gap-2">
                    <label className="aero:text-xs aero:text-muted-foreground">Minutes</label>
                    <div className="aero:flex aero:items-center aero:gap-2">
                      <button
                        type="button"
                        onClick={() => handleTimeChange("minutes", tempMinutes - 1)}
                        className="aero:p-1 aero:hover:bg-muted aero:rounded"
                      >
                        <ChevronLeft className="aero:w-4 aero:h-4" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={String(tempMinutes).padStart(2, "0")}
                        onChange={(e) => handleTimeChange("minutes", parseInt(e.target.value) || 0)}
                        className="aero:w-12 aero:text-center aero:px-2 aero:py-1 aero:border aero:border-input aero:rounded aero:bg-background aero:text-sm aero:[appearance:textfield] aero:[-moz-appearance:textfield] aero:[&::-webkit-inner-spin-button]:appearance-none aero:[&::-webkit-inner-spin-button]:m-0 aero:[&::-webkit-outer-spin-button]:appearance-none aero:[&::-webkit-outer-spin-button]:m-0"
                      />
                      <button
                        type="button"
                        onClick={() => handleTimeChange("minutes", tempMinutes + 1)}
                        className="aero:p-1 aero:hover:bg-muted aero:rounded"
                      >
                        <ChevronRight className="aero:w-4 aero:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Range info */}
            {range && (selectingRangeEnd || rangeStartDate) && (
              <div className="aero:border-t aero:border-border aero:pt-3 aero:text-xs aero:text-muted-foreground">
                {selectingRangeEnd ? (
                  <p>Select end date</p>
                ) : rangeStartDate ? (
                  <p>From: {dayjs(rangeStartDate).format("MMM DD, YYYY")}</p>
                ) : null}
              </div>
            )}

            {/* Action buttons - only show when showTime is active and not monthOnly */}
            {showTime && !monthOnly && !range && (
              <div className="aero:flex aero:gap-2 aero:border-t aero:border-border aero:pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onChange?.(null);
                  }}
                  className="aero:flex-1 aero:px-2 aero:py-1.5 aero:text-xs aero:border aero:border-input aero:rounded aero:hover:bg-muted aero:transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDateTime}
                  className="aero:flex-1 aero:px-2 aero:py-1.5 aero:text-xs aero:bg-brand-primary aero:text-brand-primary-foreground aero:rounded aero:hover:opacity-90 aero:transition-opacity"
                >
                  Confirm
                </button>
              </div>
            )}
          </div>,
          portalContainer
        )}
    </div>
  );
}
